import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Globe, Search, Shield, ShieldCheck, X } from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import PaginationFooter from '../../components/PaginationFooter';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDate } from '../../lib/format';

// Admin → All domains. Cross-tenant view of every domain across every user.
// Search matches the domain name OR the owner's email/name.
export default function DomainsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Debounce search ~300ms.
  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));

  const domainsQuery = useQuery({
    queryKey: ['admin-domains', query, page, PAGE_SIZE],
    queryFn: () => api.get(`/api/admin/domains?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const domains = domainsQuery.data?.domains || [];
  const total = domainsQuery.data?.total ?? 0;
  const totalPages = domainsQuery.data?.totalPages ?? 1;
  const hasActiveFilter = Boolean(query);

  return (
    <div className="card">
      <div className="section-header flex-wrap gap-3">
        <div className="section-title">
          <span className="section-icon">
            <Globe className="w-4 h-4" />
          </span>
          All domains
          {domainsQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10 max-w-xs"
              placeholder="Search by domain or owner…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {hasActiveFilter && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSearchInput('');
                setQuery('');
              }}
              title="Clear search"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>
      {domainsQuery.isLoading ? (
        <SkeletonTable rows={6} cols={4} wrap={false} />
      ) : !domains.length ? (
        <EmptyState
          icon={Globe}
          title={hasActiveFilter ? 'No domains match.' : 'No domains yet.'}
          body={hasActiveFilter ? 'Try a different search term.' : undefined}
        />
      ) : (
        <div className="overflow-x-auto scroll-soft">
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Owner</th>
                <th>Verified</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d._id}>
                  <td className="text-ink-800">{d.domain}</td>
                  <td className="text-ink-700">{d.user_id?.email || '—'}</td>
                  <td>
                    {d.verified ? (
                      <span className="pill-success">
                        <ShieldCheck className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="pill-warn">
                        <Shield className="w-3 h-3" /> Pending
                      </span>
                    )}
                  </td>
                  <td className="text-ink-400 whitespace-nowrap">
                    {formatDate(d.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isFetching={domainsQuery.isFetching}
      />
    </div>
  );
}
